import { stripeService } from '@/services/stripe';
import type { ICheckoutContext } from '@/types/context';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { buildStripeAmazonPayRequest } from '@/utils/amazonPay';
import type { StripePaymentResponse } from '@/types/payment';
import type { AmazonPayPaymentParams } from '@/types/paymentHandlers';

// Stripe-specific Amazon Pay handler
const handleStripeAmazonPay = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse>(params: unknown) => Promise<TResponse>,
  params: AmazonPayPaymentParams,
  amazonPaySettings: NonNullable<ReturnType<ICheckoutContext['paymentMethodsSettings']['find']>>
): Promise<StripePaymentResponse> => {
  const { elements } = params;

  const billingDetails = {
    email: checkout.customer.email,
    address: {
      country: checkout.customer.geoLocator.countryCode2,
    },
  };

  const paymentMethod = await stripeService.createAmazonPayPaymentMethod(elements, billingDetails);

  const request = buildStripeAmazonPayRequest(paymentMethod, amazonPaySettings, checkout.customer.email || '');

  const response = await sendPaymentToServer<StripePaymentResponse>(request);
  return response;
};

// Main handler that routes based on provider
export const handleAmazonPayPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse>(params: unknown) => Promise<TResponse>,
  params?: AmazonPayPaymentParams
): Promise<StripePaymentResponse> => {
  if (!params) {
    throw new Error('Amazon Pay payment requires elements parameter');
  }

  const amazonPaySettings = checkout.paymentMethodsSettings.find(
    (pm) => pm.paymentMethodName === PaymentMethodEnum.AMAZON_PAY
  );

  if (!amazonPaySettings) {
    throw new Error('Amazon Pay settings not found');
  }

  switch (amazonPaySettings.provider) {
    case ProviderEnum.STRIPE:
      return await handleStripeAmazonPay(checkout, sendPaymentToServer, params, amazonPaySettings);
    default:
      throw new Error(`Unsupported provider for Amazon Pay: ${amazonPaySettings.provider}`);
  }
};
